import Cookies from 'js-cookie';
import {PARTNER_COOKIE_NAME} from '@/utils/constant';
import {SKILLZ_APP_GAME_ID} from '@/app/(app)/partner/[slug]/_src/shellGame';

/**
 * Remap a shell-game (19369) SDK path to its clean top-level / partner-surface URL.
 *
 * The multicade surface has no `/games/19369` page — it lives at the top level (`/`,
 * `/store`, `/register`, …) or under `/partner/{slug}/…`. But every @sdk-mobile
 * navigation builds `/games/${getGameId()}/{dest}` (getGameId() = 19369 on the shell),
 * so without a remap the side menu, deposit-success register, backToRoot, etc. would
 * all dead-end on a non-existent `/games/19369/*` route. This is the single web-side
 * choke point applied in RouteHandler (see SDKProvider) so ALL SkillzRouter pushes get
 * corrected in one place. Real games (any id ≠ 19369) pass through untouched, and a
 * URL that is already clean (no `/games/19369` prefix) is returned as-is, so it is safe
 * to run on every navigation.
 *
 * `home` and the bare game root both collapse to the arcade home (`/` or
 * `/partner/{slug}`). Query/hash are preserved.
 */
/**
 * Which surface is the browser actually on right now?
 *
 * The partner cookie alone is NOT good enough: middleware only clears it on `/`, so after
 * one visit to `/partner/{slug}` it lingers, and a tile tapped on the ROOT store then
 * remapped to `/partner/{slug}/deposit/{id}` — vanilla Skillz bleeding into partner chrome.
 * The current URL is the more truthful signal, so prefer it:
 *   - `/partner/{slug}/...`  -> that slug
 *   - any other clean path (`/`, `/store`, `/account`, ...) -> ROOT, no partner, even if a
 *     stale cookie says otherwise
 *   - `/games/{id}/...`      -> the raw shell URL carries no surface information, so fall
 *     back to the cookie (this is the case the cookie exists for)
 * On the server (no window) the cookie is likewise the only signal.
 */
function currentSurfaceSlug(): string | undefined {
  if (typeof window === 'undefined') return Cookies.get(PARTNER_COOKIE_NAME);
  const path = window.location.pathname;
  const onPartner = path.match(/^\/partner\/([^/]+)/);
  if (onPartner) return onPartner[1];
  if (path.startsWith('/games/')) return Cookies.get(PARTNER_COOKIE_NAME);
  return undefined;
}

export function remapShellPath(url: string, partnerSlug?: string): string {
  const prefix = `/games/${SKILLZ_APP_GAME_ID}`;
  const isShell =
    url === prefix ||
    url.startsWith(`${prefix}/`) ||
    url.startsWith(`${prefix}?`) ||
    url.startsWith(`${prefix}#`);
  if (!isShell) return url;

  const rest = url.slice(prefix.length); // '', '/account', '/home?x=1', '?x=1'
  const qIdx = rest.search(/[?#]/);
  const query = qIdx >= 0 ? rest.slice(qIdx) : '';
  let path = qIdx >= 0 ? rest.slice(0, qIdx) : rest; // '', '/account', '/home'
  if (path.startsWith('/')) path = path.slice(1); // 'account', 'home', ''

  const slug = partnerSlug ?? currentSurfaceSlug();
  const base = slug ? `/partner/${slug}` : '';

  // Tolerate a trailing slash: Next runs trailingSlash:true, so @sdk-mobile pushes like
  // `/games/19369/home/?depositSuccess=…` (see SignupFlow post-merge redirects) arrive as
  // `home/`. Without stripping it, the collapse below misses and the user dead-ends on a
  // non-existent `/home/` (the post-deposit + SMS-merge 404). Compare against the bare path.
  const bare = path.endsWith('/') ? path.slice(0, -1) : path;
  if (bare === '' || bare === 'home') return `${base || '/'}${query}`;
  return `${base}/${path}${query}`;
}
